import React from "react";
import { ClipboardList, X } from "lucide-react";
import { joinClasses } from "./ResultsPrimitives.jsx";
import {
  formatProductUpdateDate,
  getProductUpdateBodyBlocks,
} from "../data/productUpdates.js";
import useProductUpdates from "../hooks/useProductUpdates.js";

const BANNER_TONE_CLASS_NAMES = {
  danger: {
    body: "text-red-950/80",
    closeButton: "border-red-300 bg-white/82 text-red-700 hover:bg-white hover:text-red-950",
    date: "text-red-900/66",
    eyebrow: "text-red-900/84",
    icon: "border-red-300 bg-white/78 text-red-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.62)]",
    list: "marker:text-red-700",
    section: "border-red-300 bg-[linear-gradient(180deg,rgba(255,242,242,0.99),rgba(254,226,226,0.98))] shadow-[0_20px_44px_rgba(185,28,28,0.14)]",
    title: "text-red-950",
  },

};

export default function ProductUpdateBanner({ className = "" }) {
  const {
    dismissUpdate,
    visibleAnnouncedUpdates,
  } = useProductUpdates();

  const maintenanceUpdates = visibleAnnouncedUpdates.filter(update => update?.announceTone === "danger");

  if (!maintenanceUpdates.length) {
    return null;
  }

  return (
    <div className={joinClasses([className, "relative z-30 grid w-full gap-2 overflow-y-auto pr-1"])}>
      {maintenanceUpdates.map((update) => {
        const bodyBlocks = getProductUpdateBodyBlocks(update.body);
        const bannerToneClassNames = BANNER_TONE_CLASS_NAMES.danger;

        return (
          <section
            key={update.id}
            aria-label={`Nouvelle mise à jour produit ${update.title}`}
            className={joinClasses([
              "rounded-[4px] border-2 px-5 py-4",
              bannerToneClassNames.section,
            ])}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <span className={joinClasses([
                  "mt-0.5 flex h-11 w-11 flex-none items-center justify-center rounded-[4px] border",
                  bannerToneClassNames.icon,
                ])}>
                  <ClipboardList size={18} aria-hidden="true" />
                </span>

                <div className="grid min-w-0 flex-1 gap-1.5">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <p className={joinClasses([
                      "m-0 text-[0.72rem] font-black uppercase tracking-[0.18em]",
                      bannerToneClassNames.eyebrow,
                    ])}>
                      Nouvelle mise à jour produit
                    </p>
                    <span className={joinClasses([
                      "text-[0.72rem] font-bold uppercase tracking-[0.14em]",
                      bannerToneClassNames.date,
                    ])}>
                      {formatProductUpdateDate(update.date)}
                    </span>
                  </div>

                  <h2 className={joinClasses([
                    "m-0 whitespace-pre-line text-[1.08rem] font-semibold leading-tight tracking-[-0.03em]",
                    bannerToneClassNames.title,
                  ])}>
                    {update.title}
                  </h2>
                  {bodyBlocks.length > 0 ? (
                    <div className={joinClasses([
                      "grid gap-2 text-[0.96rem] leading-6",
                      bannerToneClassNames.body,
                    ])}>
                      {bodyBlocks.map((block, index) => {
                        if (block.type === "list") {
                          return (
                            <ul
                              key={`${update.id}-list-${index}`}
                              className={joinClasses([
                                "m-0 list-disc space-y-1 pl-5",
                                bannerToneClassNames.list,
                              ])}
                            >
                              {block.items.map((item, itemIndex) => (
                                <li key={`${update.id}-list-${index}-item-${itemIndex}`}>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          );
                        }

                        return (
                          <p
                            key={`${update.id}-paragraph-${index}`}
                            className="m-0 whitespace-pre-line"
                          >
                            {block.text}
                          </p>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>

              <button
                aria-label={`Fermer l'annonce ${update.title}`}
                onClick={() => dismissUpdate(update.id)}
                className={joinClasses([
                  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[4px] border transition-colors duration-150",
                  bannerToneClassNames.closeButton,
                ])}
                title="Fermer"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
          </section>
        );
      })}
    </div>
  );
}